// src/pages/ReadjudicacionMasiva/LotePasaje.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    IconButton,
    Grid,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon,
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
    PlayArrow as PlayArrowIcon,
    People as PeopleIcon,
    Warning as WarningIcon,
    PersonOutline as PersonOutlineIcon,
    Folder as FolderIcon,
    OpenInNew as OpenInNewIcon,
    Print as PrintIcon,
    Receipt as ReceiptIcon,
    Visibility as VisibilityIcon,
    Link as LinkIcon,
    Notifications as NotificationsIcon,
    SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import useReadjudicacion from '../../hooks/useReadjudicacion';
import { usePermisos } from '../../utils/usePermisos';
import TipoDocumentoSelector from '../../components/ReadjudicacionMasiva/Documentos/TipoDocumentoSelector';
import DocumentoViewer from '../../components/ReadjudicacionMasiva/Documentos/DocumentoViewer';

const styles = {
    root: {
        backgroundColor: '#f6f8fc',
        minHeight: '100vh',
        paddingTop: '24px',
        paddingBottom: '24px'
    },
    header: {
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    loteInfo: {
        padding: '16px',
        marginBottom: '24px',
        backgroundColor: '#fafafa'
    },
    section: {
        marginBottom: '24px'
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 400
    },
    estadoChip: {
        marginLeft: '8px'
    },
    actionsBar: {
        marginTop: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px'
    },
    accordion: {
        marginBottom: '16px'
    },
    accordionSummary: {
        backgroundColor: '#fafafa'
    },
    finalizarButton: {
        minWidth: 250,
        padding: '12px 32px',
        fontWeight: 600,
        fontSize: '1.1rem'
    },
    warningBox: {
        backgroundColor: '#fff3e0',
        border: '1px solid #ffb74d',
        borderRadius: '4px',
        padding: '16px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    successBox: {
        backgroundColor: '#e8f5e9',
        border: '1px solid #81c784',
        borderRadius: '4px',
        padding: '16px',
        marginBottom: '24px'
    },
    linkExpediente: {
        color: '#5c6bc0',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline'
        },
        display: 'flex',
        alignItems: 'center',
        gap: 4
    },
    expAdminBox: {
        backgroundColor: '#e8eaf6',
        border: '1px solid #90caf9',
        borderRadius: '4px',
        padding: '16px',
        marginBottom: '16px'
    }
};

const LotePasaje = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { tienePermiso } = usePermisos();

    const {
        loading,
        lote,
        registros,
        desapoderados,
        obtenerLote,
        listarRegistros,
        generarDocumentos,
        finalizarReadjudicacion,
        obtenerDocumentosGenerados
    } = useReadjudicacion();

    const [documentosSeleccionados, setDocumentosSeleccionados] = useState([]);
    const [generando, setGenerando] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ open: false });
    const [expandedPanel, setExpandedPanel] = useState('resumen');
    const [recibosGenerados, setRecibosGenerados] = useState([]);
    const [documentoViewer, setDocumentoViewer] = useState({ open: false, titulo: '', contenido: '', nombreArchivo: '' });
    const [documentosFinalizacion, setDocumentosFinalizacion] = useState([]);
    const [dialogFinalizacion, setDialogFinalizacion] = useState({ open: false });
    const [resumenFinalizacion, setResumenFinalizacion] = useState(null);

    // Estado para controlar si el lote esta finalizado (solo lectura)
    const loteFinalizado = lote && lote.Estado === 5;
    const loteListoParaPasaje = lote && lote.Estado === 4;

    // Verificar permisos
    useEffect(() => {
        if (!tienePermiso('READJUDICACION MASIVA', 'UPDATE')) {
            enqueueSnackbar('No tiene permisos para gestionar el pasaje', { variant: 'error' });
            navigate('/gestion-lotes?tab=readjudicacion');
        }
    }, [tienePermiso, navigate, enqueueSnackbar]);

    // Cargar datos del lote
    useEffect(() => {
        if (id) {
            cargarDatos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Cargar recibos cuando el lote está finalizado
    useEffect(() => {
        if (lote && lote.Estado === 5) {
            cargarRecibos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lote]);

    const cargarDatos = async () => {
        await Promise.all([
            obtenerLote(id),
            listarRegistros(id)
        ]);
    };

    const cargarRecibos = async () => {
        const docs = await obtenerDocumentosGenerados(id, 'RECIBO');
        setRecibosGenerados(docs);
    };

    const handleVolverEditar = () => {
        navigate(`/readjudicacion-masiva/abogados/${id}`);
    };

    const handleVolver = () => {
        navigate('/gestion-lotes?tab=readjudicacion');
    };

    // Abrir expediente judicial en nueva ventana
    const handleAbrirExpediente = (idExpediente) => {
        if (idExpediente) {
            window.open(`/expediente/judicial/${idExpediente}`, '_blank');
        }
    };

    // Abrir expediente administrativo en nueva ventana
    const handleAbrirExpedienteAdmin = () => {
        if (lote?.Id_Exp_Adm) {
            window.open(`/expediente/administrativo/${lote.Id_Exp_Adm}`, '_blank');
        }
    };

    // Ver/Imprimir un recibo individual en modal
    const handleVerRecibo = (recibo) => {
        setDocumentoViewer({
            open: true,
            titulo: `Readjudicacion - Recibo - ${recibo.AbogadoNombre || 'Abogado'}`,
            contenido: recibo.Contenido,
            nombreArchivo: `Recibo_${recibo.AbogadoNombre || 'Abogado'}_Lote_${lote?.Id || id}`.replace(/\s+/g, '_')
        });
    };

    // Cerrar el visor de documentos
    const handleCerrarViewer = () => {
        setDocumentoViewer({ open: false, titulo: '', contenido: '', nombreArchivo: '' });
    };

    // Imprimir todos los recibos usando iframe oculto
    const handleImprimirTodosRecibos = () => {
        if (recibosGenerados.length === 0) {
            enqueueSnackbar('No hay recibos para imprimir', { variant: 'warning' });
            return;
        }

        // Crear iframe oculto para imprimir sin abrir nueva ventana
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'absolute';
        printFrame.style.top = '-10000px';
        printFrame.style.left = '-10000px';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = 'none';
        document.body.appendChild(printFrame);

        const printDocument = printFrame.contentWindow || printFrame.contentDocument;
        const doc = printDocument.document || printDocument;

        let htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Recibos - Lote ${lote?.Id || id}</title>
                <style>
                    @media print {
                        .page-break { page-break-after: always; }
                    }
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .documento { margin-bottom: 40px; padding: 20px; }
                    .documento-header { background: #f5f5f5; padding: 10px; margin-bottom: 20px; font-weight: bold; }
                </style>
            </head>
            <body>
        `;

        recibosGenerados.forEach((recibo, index) => {
            htmlContent += `
                <div class="documento ${index < recibosGenerados.length - 1 ? 'page-break' : ''}">
                    <div class="documento-contenido">${recibo.Contenido}</div>
                </div>
            `;
        });

        htmlContent += '</body></html>';

        doc.open();
        doc.write(htmlContent);
        doc.close();

        // Esperar a que el contenido cargue y luego imprimir
        setTimeout(() => {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            // Limpiar el iframe despues de un tiempo
            setTimeout(() => {
                if (printFrame.parentNode) {
                    printFrame.parentNode.removeChild(printFrame);
                }
            }, 1000);
        }, 250);
    };

    const handleFinalizarYGenerar = async () => {
        if (documentosSeleccionados.length === 0) {
            enqueueSnackbar('Debe seleccionar al menos un tipo de documento', { variant: 'warning' });
            return;
        }
        setConfirmDialog({ open: true });
    };

    const handleConfirmarFinalizacion = async () => {
        setConfirmDialog({ open: false });
        setGenerando(true);

        try {
            // 1. Generar documentos
            const resultadoDocs = await generarDocumentos(
                id,
                documentosSeleccionados,
                { formato: 'PDF', ordenamiento: 'FILA', copias: 1 }
            );

            if (!resultadoDocs || !resultadoDocs.ok) {
                throw new Error(resultadoDocs?.mensaje || 'Error al generar documentos');
            }

            // 2. Guardar documentos para mostrar en dialogo
            if (resultadoDocs.documentos && resultadoDocs.documentos.length > 0) {
                setDocumentosFinalizacion(resultadoDocs.documentos);
            }

            // 3. Finalizar readjudicacion (insertar movimientos, actualizar partes, etc.)
            const resultadoFinal = await finalizarReadjudicacion(id);

            if (resultadoFinal && resultadoFinal.ok) {
                enqueueSnackbar('Lote finalizado correctamente. Los documentos se han generado y los cambios aplicados.', { variant: 'success' });
                setResumenFinalizacion(resultadoFinal);
                setDialogFinalizacion({ open: true });
            } else {
                throw new Error(resultadoFinal?.mensaje || 'Error al finalizar el lote');
            }
        } catch (error) {
            enqueueSnackbar(error.message || 'Error al procesar el lote', { variant: 'error' });
        } finally {
            setGenerando(false);
        }
    };

    // Cerrar dialogo de finalizacion y recargar datos
    const handleCerrarDialogFinalizacion = async () => {
        setDialogFinalizacion({ open: false });
        setDocumentosFinalizacion([]);
        // Recargar datos para mostrar estado finalizado
        await cargarDatos();
    };

    const getEstadoChip = () => {
        if (!lote) return null;

        const estados = {
            0: { label: 'Pendiente Auditoria', color: 'warning' },
            1: { label: 'Auditado', color: 'primary' },
            2: { label: 'Desapoderado', color: 'secondary' },
            3: { label: 'Asignado', color: 'info' },
            4: { label: 'Listo para Pasaje', style: { backgroundColor: '#fce4ec', color: '#c62828' } },
            5: { label: 'Finalizado', color: 'success' }
        };

        const estado = estados[lote.Estado] || { label: 'Desconocido', color: 'warning' };
        return <Chip label={estado.label} size="small" color={estado.color} variant="filled" style={estado.style} sx={styles.estadoChip} />;
    };

    const registrosValidos = registros.filter(r => r.Ok === 1 || r.Ok === 4);
    const registrosInvalidos = registros.filter(r => r.Ok !== 1 && r.Ok !== 4);

    // Obtener resumen de abogados (solo de registros validos)
    const resumenAbogados = registrosValidos.reduce((acc, reg) => {
        if (reg.AbogadoAsignado) {
            if (!acc[reg.AbogadoAsignado]) {
                acc[reg.AbogadoAsignado] = 0;
            }
            acc[reg.AbogadoAsignado]++;
        }
        return acc;
    }, {});

    const cantidadesPorDocumento = documentosSeleccionados.reduce((acc, tipo) => {
        acc[tipo] = registrosValidos.length;
        return acc;
    }, {});

    if (loading && !lote) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                <Box sx={styles.loadingBox}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (!lote) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                <Typography variant="h6" color="error">
                    Lote no encontrado
                </Typography>
            </Container>
        );
    }

    // Pantalla de solo lectura si el lote esta finalizado
    if (loteFinalizado) {
        return (
            <Container maxWidth={false} sx={styles.root}>
                {/* Header */}
                <Box sx={styles.header}>
                    <IconButton onClick={handleVolver} size="small" style={{ color: '#5c6bc0' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" style={{ fontWeight: 700, color: '#5c6bc0', flex: 1 }}>
                        Lote {lote.Id} - Finalizado
                    </Typography>
                    {getEstadoChip()}
                </Box>

                {/* Informacion del lote */}
                <Paper sx={styles.loteInfo}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Expediente Administrativo
                            </Typography>
                            {lote.Id_Exp_Adm ? (
                                <Typography variant="body1" style={{ fontWeight: 600 }} sx={styles.linkExpediente} onClick={handleAbrirExpedienteAdmin}>
                                    {lote.ExpedienteAdministrativo || '-'} <OpenInNewIcon style={{ fontSize: 14 }} />
                                </Typography>
                            ) : (
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                    {lote.ExpedienteAdministrativo || '-'}
                                </Typography>
                            )}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Total Registros
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 600 }}>
                                {lote.TotalRegistros || 0}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Registros Procesados
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 600, color: '#4caf50' }}>
                                {registrosValidos.length}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                                Fecha Finalizado
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 600 }}>
                                {lote.Fecha ? new Date(lote.Fecha).toLocaleDateString('es-AR') : '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Mensaje de lote finalizado */}
                <Box sx={styles.successBox}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <CheckCircleIcon style={{ color: '#4caf50', fontSize: 32 }} />
                        <Box>
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                Lote finalizado exitosamente
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Los documentos fueron generados y los cambios aplicados a los expedientes correspondientes.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Panel izquierdo - Informacion del lote */}
                    <Grid size={12}>
                        {/* Resumen de Abogados */}
                        <Accordion
                            expanded={expandedPanel === 'resumen'}
                            onChange={() => setExpandedPanel(expandedPanel === 'resumen' ? '' : 'resumen')}
                            sx={styles.accordion}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                                <PeopleIcon style={{ marginRight: 8 }} />
                                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                    Resumen de Asignaciones ({Object.keys(resumenAbogados).length} abogados)
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ fontWeight: 600 }}>Abogado Asignado</TableCell>
                                                <TableCell align="center" style={{ fontWeight: 600 }}>Expedientes</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(resumenAbogados).map(([abogado, cantidad], idx) => (
                                                <TableRow key={abogado} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                    <TableCell>{abogado}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={cantidad} size="small" color="primary" />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {Object.keys(resumenAbogados).length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={2} align="center">
                                                        <Typography variant="body2" color="text.secondary">
                                                            No hay asignaciones registradas
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>

                        {/* Lista de Expedientes */}
                        <Accordion
                            expanded={expandedPanel === 'expedientes'}
                            onChange={() => setExpandedPanel(expandedPanel === 'expedientes' ? '' : 'expedientes')}
                            sx={styles.accordion}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                                <DescriptionIcon style={{ marginRight: 8 }} />
                                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                    Expedientes Procesados ({registrosValidos.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer style={{ maxHeight: 400 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ fontWeight: 600 }}>Expediente</TableCell>
                                                <TableCell style={{ fontWeight: 600 }}>Caratula</TableCell>
                                                <TableCell style={{ fontWeight: 600 }}>Abogado Asignado</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {registrosValidos.slice(0, 50).map((reg, idx) => (
                                                <TableRow key={reg.Id} hover sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                    <TableCell>
                                                        {reg.Id_Expediente ? (
                                                            <Box
                                                                component="span"
                                                                sx={styles.linkExpediente}
                                                                onClick={() => handleAbrirExpediente(reg.Id_Expediente)}
                                                            >
                                                                {reg.Campo1 || reg.Autos || '-'}
                                                                <OpenInNewIcon style={{ fontSize: 14 }} />
                                                            </Box>
                                                        ) : (
                                                            reg.Campo1 || reg.Autos || '-'
                                                        )}
                                                    </TableCell>
                                                    <TableCell style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {reg.Caratula || reg.Campo2 || '-'}
                                                    </TableCell>
                                                    <TableCell>{reg.AbogadoAsignado || '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                            {registrosValidos.length > 50 && (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center">
                                                        <Typography variant="body2" color="text.secondary">
                                                            ... y {registrosValidos.length - 50} expedientes mas
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>

                        {/* Abogados Desapoderados */}
                        <Accordion
                            expanded={expandedPanel === 'desapoderados'}
                            onChange={() => setExpandedPanel(expandedPanel === 'desapoderados' ? '' : 'desapoderados')}
                            sx={styles.accordion}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                                <PersonOutlineIcon style={{ marginRight: 8, color: '#f44336' }} />
                                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                    Abogados Desapoderados ({desapoderados?.length || 0})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ fontWeight: 600 }}>Profesional</TableCell>
                                                <TableCell style={{ fontWeight: 600 }}>Matricula</TableCell>
                                                <TableCell align="center" style={{ fontWeight: 600 }}>Expedientes</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {desapoderados && desapoderados.map((desap, index) => (
                                                <TableRow key={`desap-fin-${desap.DNI || index}`} sx={{ backgroundColor: index % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                    <TableCell>{desap.Profesional || '-'}</TableCell>
                                                    <TableCell>{desap.Matricula || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={desap.CantidadExpedientes || 0} size="small" style={{ backgroundColor: '#ffebee', color: '#c62828' }} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(!desapoderados || desapoderados.length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center">
                                                        <Typography variant="body2" color="text.secondary">
                                                            No hay abogados desapoderados en este lote
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>

                        {/* Expediente Administrativo */}
                        {lote?.ExpedienteAdministrativo && (
                            <Accordion
                                expanded={expandedPanel === 'expAdmin'}
                                onChange={() => setExpandedPanel(expandedPanel === 'expAdmin' ? '' : 'expAdmin')}
                                sx={styles.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                                    <FolderIcon style={{ marginRight: 8, color: '#5c6bc0' }} />
                                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                        Expediente Administrativo Vinculado
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={styles.expAdminBox} style={{ width: '100%' }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid size={{ xs: 12, md: 8 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Numero de Expediente
                                                </Typography>
                                                <Typography variant="h6" style={{ fontWeight: 600 }}>
                                                    {lote.ExpedienteAdministrativo}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<OpenInNewIcon />}
                                                    onClick={handleAbrirExpedienteAdmin}
                                                    fullWidth
                                                >
                                                    Abrir Expediente
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* Recibos Generados */}
                        <Accordion
                            expanded={expandedPanel === 'recibos'}
                            onChange={() => setExpandedPanel(expandedPanel === 'recibos' ? '' : 'recibos')}
                            sx={styles.accordion}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                                <ReceiptIcon style={{ marginRight: 8, color: '#9c27b0' }} />
                                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                    Recibos Generados ({recibosGenerados.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box style={{ width: '100%' }}>
                                    {recibosGenerados.length > 0 ? (
                                        <>
                                            <Box display="flex" justifyContent="flex-end" mb={2}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<PrintIcon />}
                                                    onClick={handleImprimirTodosRecibos}
                                                >
                                                    Imprimir Todos
                                                </Button>
                                            </Box>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell style={{ fontWeight: 600 }}>Abogado</TableCell>
                                                            <TableCell style={{ fontWeight: 600 }}>Matricula</TableCell>
                                                            <TableCell align="center" style={{ fontWeight: 600 }}>Acciones</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {recibosGenerados.map((recibo, idx) => (
                                                            <TableRow key={recibo.Id} hover sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                                <TableCell>{recibo.AbogadoNombre || '-'}</TableCell>
                                                                <TableCell>{recibo.AbogadoMatricula || '-'}</TableCell>
                                                                <TableCell align="center">
                                                                    <Button
                                                                        size="small"
                                                                        color="primary"
                                                                        startIcon={<VisibilityIcon />}
                                                                        onClick={() => handleVerRecibo(recibo)}
                                                                    >
                                                                        Ver / Imprimir
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" align="center">
                                            No hay recibos generados para este lote
                                        </Typography>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Boton para volver */}
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleVolver}
                                startIcon={<ArrowBackIcon />}
                            >
                                Volver al Dashboard
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                {/* Modal para ver documentos */}
                <DocumentoViewer
                    open={documentoViewer.open}
                    onClose={handleCerrarViewer}
                    titulo={documentoViewer.titulo}
                    contenido={documentoViewer.contenido}
                    nombreArchivo={documentoViewer.nombreArchivo}
                />
            </Container>
        );
    }

    // Pantalla de Listos para Pasaje (editable)
    return (
        <Container maxWidth={false} sx={styles.root}>
            {/* Header */}
            <Box sx={styles.header}>
                <IconButton onClick={handleVolver} size="small" style={{ color: '#5c6bc0' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" style={{ fontWeight: 700, color: '#5c6bc0', flex: 1 }}>
                    Pasaje - Lote {lote.Id}
                </Typography>
                {getEstadoChip()}
            </Box>

            {/* Informacion del lote */}
            <Paper sx={styles.loteInfo}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Expediente Administrativo
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {lote.ExpedienteAdministrativo || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Total Registros
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {lote.TotalRegistros || 0}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Registros Validos
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600, color: '#4caf50' }}>
                            {registrosValidos.length}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Registros Invalidos
                        </Typography>
                        <Typography variant="body1" style={{ fontWeight: 600, color: registrosInvalidos.length > 0 ? '#f44336' : '#9e9e9e' }}>
                            {registrosInvalidos.length}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Aviso de listo para pasaje */}
            {loteListoParaPasaje && (
                <Box sx={styles.warningBox}>
                    <WarningIcon style={{ color: '#ff9800', fontSize: 32 }} />
                    <Box>
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                            Lote listo para finalizar
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Revise los datos antes de finalizar. Una vez finalizado, se generaran los documentos y se aplicaran los cambios a los expedientes.
                            Si necesita modificar algo, puede volver a la pantalla de abogados.
                        </Typography>
                    </Box>
                </Box>
            )}

            <Grid container spacing={3}>
                {/* Panel izquierdo - Resumen */}
                <Grid size={{ xs: 12, lg: 7 }}>
                    {/* Resumen de Abogados */}
                    <Accordion
                        expanded={expandedPanel === 'resumen'}
                        onChange={() => setExpandedPanel(expandedPanel === 'resumen' ? '' : 'resumen')}
                        sx={styles.accordion}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                            <PeopleIcon style={{ marginRight: 8 }} />
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                Resumen de Asignaciones ({Object.keys(resumenAbogados).length} abogados)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 600 }}>Abogado Asignado</TableCell>
                                            <TableCell align="center" style={{ fontWeight: 600 }}>Expedientes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(resumenAbogados).map(([abogado, cantidad], idx) => (
                                            <TableRow key={abogado} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                <TableCell>{abogado}</TableCell>
                                                <TableCell align="center">
                                                    <Chip label={cantidad} size="small" color="primary" />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {Object.keys(resumenAbogados).length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No hay asignaciones registradas
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>

                    {/* Lista de Expedientes */}
                    <Accordion
                        expanded={expandedPanel === 'expedientes'}
                        onChange={() => setExpandedPanel(expandedPanel === 'expedientes' ? '' : 'expedientes')}
                        sx={styles.accordion}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                            <DescriptionIcon style={{ marginRight: 8 }} />
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                Expedientes ({registrosValidos.length} validos)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer style={{ maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 600 }}>Expediente</TableCell>
                                            <TableCell style={{ fontWeight: 600 }}>Caratula</TableCell>
                                            <TableCell style={{ fontWeight: 600 }}>Abogado Asignado</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {registrosValidos.slice(0, 50).map((reg, idx) => (
                                            <TableRow key={reg.Id} hover sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                <TableCell>
                                                    {reg.Id_Expediente ? (
                                                        <Box
                                                            component="span"
                                                            sx={styles.linkExpediente}
                                                            onClick={() => handleAbrirExpediente(reg.Id_Expediente)}
                                                        >
                                                            {reg.Campo1 || reg.Autos || '-'}
                                                            <OpenInNewIcon style={{ fontSize: 14 }} />
                                                        </Box>
                                                    ) : (
                                                        reg.Campo1 || reg.Autos || '-'
                                                    )}
                                                </TableCell>
                                                <TableCell style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {reg.Caratula || reg.Campo2 || '-'}
                                                </TableCell>
                                                <TableCell>{reg.AbogadoAsignado || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                        {registrosValidos.length > 50 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        ... y {registrosValidos.length - 50} expedientes mas
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>

                    {/* Abogados Desapoderados */}
                    <Accordion
                        expanded={expandedPanel === 'desapoderados'}
                        onChange={() => setExpandedPanel(expandedPanel === 'desapoderados' ? '' : 'desapoderados')}
                        sx={styles.accordion}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                            <PersonOutlineIcon style={{ marginRight: 8, color: '#f44336' }} />
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                Abogados Desapoderados ({desapoderados?.length || 0})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 600 }}>Profesional</TableCell>
                                            <TableCell style={{ fontWeight: 600 }}>Matricula</TableCell>
                                            <TableCell align="center" style={{ fontWeight: 600 }}>Expedientes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {desapoderados && desapoderados.map((desap, index) => (
                                            <TableRow key={`desap-${desap.DNI || index}`} sx={{ backgroundColor: index % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                <TableCell>{desap.Profesional || '-'}</TableCell>
                                                <TableCell>{desap.Matricula || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Chip label={desap.CantidadExpedientes || 0} size="small" style={{ backgroundColor: '#ffebee', color: '#c62828' }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(!desapoderados || desapoderados.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No hay abogados desapoderados en este lote
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>

                    {/* Expediente Administrativo */}
                    {lote?.ExpedienteAdministrativo && (
                        <Accordion
                            expanded={expandedPanel === 'expAdmin'}
                            onChange={() => setExpandedPanel(expandedPanel === 'expAdmin' ? '' : 'expAdmin')}
                            sx={styles.accordion}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                                <FolderIcon style={{ marginRight: 8, color: '#5c6bc0' }} />
                                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                    Expediente Administrativo Vinculado
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={styles.expAdminBox} style={{ width: '100%' }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid size={{ xs: 12, md: 8 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Numero de Expediente
                                            </Typography>
                                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                                {lote.ExpedienteAdministrativo}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<OpenInNewIcon />}
                                                onClick={handleAbrirExpedienteAdmin}
                                                fullWidth
                                            >
                                                Abrir Expediente
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Boton para volver a editar */}
                    <Box display="flex" justifyContent="flex-start" mt={2}>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={handleVolverEditar}
                            disabled={generando}
                            style={{ backgroundColor: 'white', color: '#5c6bc0' }}
                        >
                            Modificar Asignaciones
                        </Button>
                    </Box>
                </Grid>

                {/* Panel derecho - Documentos y Finalizar */}
                <Grid size={{ xs: 12, lg: 5 }}>
                    <Paper style={{ padding: 24 }}>
                        <Typography variant="h6" sx={styles.sectionTitle}>
                            <DescriptionIcon />
                            Seleccionar Documentos a Generar
                        </Typography>

                        <TipoDocumentoSelector
                            seleccionados={documentosSeleccionados}
                            onChange={setDocumentosSeleccionados}
                            cantidades={cantidadesPorDocumento}
                        />

                        <Divider style={{ margin: '24px 0' }} />

                        {/* Resumen de documentos */}
                        {documentosSeleccionados.length > 0 && (
                            <Box sx={styles.successBox} mb={3}>
                                <Typography variant="body2">
                                    Se generaran <strong>{documentosSeleccionados.length * registrosValidos.length}</strong> documento(s)
                                    para <strong>{registrosValidos.length}</strong> expediente(s).
                                </Typography>
                            </Box>
                        )}

                        {/* Boton de finalizar */}
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            sx={styles.finalizarButton}
                            startIcon={generando ? <CircularProgress size={24} color="inherit" /> : <PlayArrowIcon />}
                            onClick={handleFinalizarYGenerar}
                            disabled={generando || loading || documentosSeleccionados.length === 0}
                        >
                            {generando ? 'Procesando...' : 'Generar Documentos y Finalizar'}
                        </Button>

                        {documentosSeleccionados.length === 0 && (
                            <Typography variant="caption" color="text.secondary" align="center" display="block" mt={1}>
                                Seleccione al menos un documento para continuar
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Dialog de confirmacion */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => !generando && setConfirmDialog({ open: false })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <WarningIcon style={{ color: '#ff9800' }} />
                        Confirmar Finalizacion
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Esta a punto de finalizar el lote de readjudicacion. Esta accion realizara lo siguiente:
                    </Typography>
                    <Box component="ul" pl={2}>
                        <li><Typography variant="body2">Generar {documentosSeleccionados.length * registrosValidos.length} documento(s)</Typography></li>
                        <li><Typography variant="body2">Insertar movimientos en cada expediente</Typography></li>
                        <li><Typography variant="body2">Actualizar las partes (desapoderar y apoderar abogados)</Typography></li>
                        <li><Typography variant="body2">Registrar el lote en cada expediente</Typography></li>
                    </Box>
                    <Typography variant="body2" color="error" style={{ marginTop: 16, fontWeight: 600 }}>
                        Una vez finalizado, no se podran realizar mas cambios en este lote.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false })} disabled={generando}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmarFinalizacion}
                        color="primary"
                        variant="contained"
                        disabled={generando}
                    >
                        {generando ? 'Procesando...' : 'Confirmar y Finalizar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de resumen de finalizacion */}
            <Dialog
                open={dialogFinalizacion.open}
                onClose={handleCerrarDialogFinalizacion}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon style={{ color: '#4caf50' }} />
                        Lote Finalizado Correctamente
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#e8f5e9', borderRadius: 4 }}>
                            <SwapHorizIcon style={{ color: '#4caf50', marginRight: 12 }} />
                            <Typography variant="body1">
                                Expedientes procesados: <strong>{resumenFinalizacion?.expedientesProcesados || 0}</strong>
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#e8f5e9', borderRadius: 4 }}>
                            <DescriptionIcon style={{ color: '#4caf50', marginRight: 12 }} />
                            <Typography variant="body1">
                                Cartas Poder enviadas para firma: <strong>{documentosFinalizacion.filter(d => d.tipo?.startsWith('CARTA_PODER')).length}</strong>
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#e8eaf6', borderRadius: 4 }}>
                            <ReceiptIcon style={{ color: '#5c6bc0', marginRight: 12 }} />
                            <Typography variant="body1">
                                Recibos adjuntados en expedientes: <strong>{resumenFinalizacion?.recibosInsertados || 0}</strong>
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#fff3e0', borderRadius: 4 }}>
                            <LinkIcon style={{ color: '#e65100', marginRight: 12 }} />
                            <Typography variant="body1">
                                Vinculaciones creadas: <strong>{resumenFinalizacion?.vinculaciones || 0}</strong>
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" p={1.5} mb={1} style={{ backgroundColor: '#f3e5f5', borderRadius: 4 }}>
                            <NotificationsIcon style={{ color: '#7b1fa2', marginRight: 12 }} />
                            <Typography variant="body1">
                                Notificaciones enviadas: <strong>{resumenFinalizacion?.notificacionesEnviadas || 0}</strong>
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCerrarDialogFinalizacion}
                        color="primary"
                        variant="contained"
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal para ver documentos */}
            <DocumentoViewer
                open={documentoViewer.open}
                onClose={handleCerrarViewer}
                titulo={documentoViewer.titulo}
                contenido={documentoViewer.contenido}
                nombreArchivo={documentoViewer.nombreArchivo}
            />
        </Container>
    );
};

export default LotePasaje;
